'use client';

import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { useSession } from 'next-auth/react';
import { Box, TextField } from '@mui/material';
import { isEqual, uniqueId } from 'lodash';

import {
  StyledCampaignFormRow,
  StyledCampaignFormSubtitle,
  useUpdateCampaign,
} from '~/entities/Campaign';
import { SelectState } from '~/entities/SegmentsWithCity';
import { CitySelection } from '~/entities/SegmentsWithCity/components/CitySelection';
import { CnpjActionsRow } from '~/entities/SegmentsWithCity/components/CnpjActionsRow';
import { LoadingOverlay } from '~/entities/SegmentsWithCity/components/LoadingOverlay';
import { SegmentActions } from '~/entities/SegmentsWithCity/components/SegmentActions';
import SegmentRow from '~/entities/SegmentsWithCity/components/SegmentRow';
import { SubcategorySelection } from '~/entities/SegmentsWithCity/components/SubcategorySelection';
import {
  CampaignDeleteCnpjExcelModal,
  CampaignErrorExcelModal,
  CampaignUpdatedExcelModal,
} from '~/entities/SegmentsWithCity/dialogs';
import type {
  CampaignSegment,
  CampaignSegmentOption,
  SegmentFromListCampaign,
} from '~/types';

import { useGetProduct } from '../../hooks';
import { deleteCnjpsListProducts } from '../../hooks/useDeleteCnpjList';
import { downloadCurrentCnjpsList } from '../../hooks/useGetDownloadCurrentListCNPJ';
import { downloadDefaultCnpjListProducts } from '../../hooks/useGetDownloadDefaultListCNPJ';
import { useUploadCnpjSponsoredProductsExcel } from '../../hooks/useUploadCnpjExcel';

const SEGMENTS = [
  { id: '1', name: 'Estado', key: 'estado' },
  { id: '2', name: 'Canais de mídia', key: 'canaisMidia' },
  { id: '3', name: 'Categorias', key: 'categorias' },
  { id: '4', name: 'CNPJ e CPF', key: 'cnpj' },
];

export function isSegmentEmpty(segments: CampaignSegment | undefined) {
  let isEmpty = !segments ? true : Object.keys(segments).length === 0;
  if (!isEmpty && segments) {
    isEmpty = Object.keys(segments).every(
      key => segments[key as keyof CampaignSegment].length === 0
    );
  }
  return isEmpty;
}

export function RowSegments({
  campaign,
  updateCampaignQuery,
}: {
  campaign: ReturnType<typeof useGetProduct>['data'];
  updateCampaignQuery: ReturnType<typeof useUpdateCampaign>;
}) {
  const session = useSession();
  const [segments, setSegments] = useState<SegmentFromListCampaign[]>([]);

  const [blockedSegments, setBlockedSegments] = useState<string[]>([]);
  const [selectedStates, setSelectedState] = useState<CampaignSegmentOption[]>(
    []
  );
  const [selectedCategories, setSelectedCategories] = useState<
    CampaignSegmentOption[]
  >([]);
  const [isCNPJSelected, setIsCNPJSelected] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'success' | 'error' | 'loading'
  >('idle');
  const [deleteStatus, setDeleteStatus] = useState<
    'idle' | 'success' | 'error' | 'loading'
  >('idle');
  const [responseInfo, setResponseInfo] = useState<{
    upload?: { mensagem?: string | null };
    delete?: { message?: string | null };
  }>({});

  const [isDeleteCnpjList, setIsDeleteCnpjList] = useState(false);
  const getCampaignSponsoredQuery = useGetProduct(campaign!.id);
  const [isFirstSelectionSegment, setIsFirstSelectionSegment] = useState(true);

  const isCnpjList = getCampaignSponsoredQuery?.data?.hasCnpjSegmented;
  const uploadCnpjSponsoredProductsExcel =
    useUploadCnpjSponsoredProductsExcel();

  const handleAddSegment = (index: number) => {
    if (isCNPJSelected) return;

    setSegments(prevSegments => [
      ...prevSegments.slice(0, index + 1),
      { id: uniqueId() },
      ...prevSegments.slice(index + 1),
    ]);

    setIsFirstSelectionSegment(false);
  };

  function handleRemoveSegment(index: number) {
    if (index === 1) {
      setIsFirstSelectionSegment(true);
    }

    const removedSegmentKey = segments[index].segmentType?.key;
    setSegments(state => state.filter((_, i) => i !== index));
    setBlockedSegments(state => state.filter(key => key !== removedSegmentKey));
  }

  const handleUpload = async (selectedFile: File) => {
    const token = session?.data?.t.st;

    const formData = new FormData();
    formData.append('file', selectedFile);

    const { id } = campaign!;

    try {
      const response = await uploadCnpjSponsoredProductsExcel.mutateAsync({
        file: selectedFile,
        id,
        token,
      });

      setResponseInfo(prev => ({ ...prev, upload: response }));
      setUploadStatus('success');
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('error');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      await handleUpload(selectedFile);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  function handleOnCloseModalUploadSuccess() {
    setUploadStatus('idle');
  }

  function handleOnCloseModalDeleteSuccess() {
    setIsDeleteCnpjList(false);
  }

  function handleOnCloseModalError() {
    setUploadStatus('idle');
  }

  useEffect(() => {
    if (!campaign?.segments) return;

    if (segments.length === 0 && isSegmentEmpty(campaign.segments)) {
      setSegments([{ id: uniqueId() }]);
    }
    if (!isSegmentEmpty(campaign.segments) && segments.length === 0) {
      const segmentsFromCampaign = Object.keys(campaign.segments)
        .map(key => {
          const isEmpty =
            campaign.segments[key as keyof CampaignSegment].length === 0;
          if (isEmpty) return null;

          // Transform backend keys to frontend keys
          let frontendKey: string;
          if (key === 'categoria') {
            frontendKey = 'categorias';
          } else if (key === 'midia' || key === 'canais') {
            frontendKey = 'canaisMidia';
          } else {
            frontendKey = key;
          }

          return {
            id: uniqueId(),
            segmentType:
              SEGMENTS.find(segment => segment.key === frontendKey) ?? null,
            segmentSelections:
              campaign.segments[key as keyof CampaignSegment] ?? null,
          };
        })
        .filter(item => item !== null) as SegmentFromListCampaign[];

      setSegments(segmentsFromCampaign);

      // Initialize selectedStates and selectedCategories for loaded segments
      segmentsFromCampaign.forEach(segmentField => {
        if (segmentField.segmentType?.name === 'Estado') {
          setSelectedState(segmentField.segmentSelections ?? []);
        }
        if (segmentField.segmentType?.name === 'Categorias') {
          setSelectedCategories(segmentField.segmentSelections ?? []);
        }
      });
    }
  }, [campaign?.segments, segments.length]);

  const handleDownload = async () => {
    try {
      const response = await downloadCurrentCnjpsList(campaign!.id);

      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'CNPJ-CPF-Active-Export';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadDefault = async () => {
    try {
      const response = await downloadDefaultCnpjListProducts(campaign?.id);

      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'CNPJ-CPF-Template-Export';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteStatus('loading');
      const response = await deleteCnjpsListProducts(campaign!.id);
      setResponseInfo({
        delete: {
          message: response.data.message,
        },
      });
      setDeleteStatus('success');
      setIsDeleteCnpjList(true);
    } catch (err) {
      console.error(err);
      setDeleteStatus('error');
    }
  };

  const handleUpdateAfterUpload = async () => {
    await getCampaignSponsoredQuery.refetch();
    setUploadStatus('idle');
  };

  const handleUpdateAfterDelete = async () => {
    await getCampaignSponsoredQuery.refetch();
    setIsDeleteCnpjList(false);
    setDeleteStatus('idle');
  };

  useEffect(() => {
    const newBlockedSegments = segments
      .map(item => item.segmentType?.key)
      .filter(item => item !== undefined) as string[];

    setBlockedSegments(newBlockedSegments);
  }, [segments]);

  useDebounce(
    () => {
      if (!campaign?.segments) return;

      const allSegmentsFromCampaign =
        Object.keys(campaign.segments)?.flatMap(key => {
          return campaign.segments[key as keyof CampaignSegment].flatMap(
            segment => {
              const { id: segmentId } = segment;
              // Handle estado with cidades
              if (key === 'estado' && 'cidades' in segment) {
                const { cidades = [] } = segment;
                if (cidades.length === 0) {
                  return [{ id: segmentId }];
                }
                return cidades.map(({ id: cidadeId }) => ({
                  id: segmentId,
                  cidadeId,
                }));
              }
              // Handle categorias with subcategorias
              if (key === 'categorias' && 'subcategorias' in segment) {
                const { subcategorias = [] } = segment;
                if (subcategorias.length === 0) {
                  return [{ id: segmentId }];
                }
                return subcategorias.map(({ id: subCategoriaId }) => ({
                  id: segmentId,
                  subCategoriaId,
                }));
              }
              return { id: segmentId };
            }
          );
        }) ?? [];

      const allSegmentsSelectionIds = segments.flatMap(
        ({ segmentSelections }) => {
          return (
            segmentSelections?.flatMap(
              ({ id: segmentId, cidades = [], subcategorias = [] }) => {
                // Handle estado with cidades
                if (cidades.length > 0) {
                  return cidades.map(({ id: cidadeId }) => ({
                    id: segmentId,
                    cidadeId,
                  }));
                }
                // Handle categorias with subcategorias
                if (subcategorias.length > 0) {
                  return subcategorias.map(({ id: subCategoriaId }) => ({
                    id: segmentId,
                    subCategoriaId,
                  }));
                }
                return [{ id: segmentId }];
              }
            ) ?? []
          );
        }
      );

      segments.forEach(segmentField => {
        if (segmentField.segmentType?.name === 'Estado') {
          setSelectedState(segmentField.segmentSelections ?? []);
        }
        if (segmentField.segmentType?.name === 'Categorias') {
          setSelectedCategories(segmentField.segmentSelections ?? []);
        }
      });

      if (!isEqual(allSegmentsFromCampaign, allSegmentsSelectionIds)) {
        if (campaign) {
          // Build segments with proper tipo for each one
          const segmentsWithTipo = segments.flatMap(segment => {
            const segmentKey = segment.segmentType?.key;

            let tipo: string | null = null;
            if (segmentKey === 'categorias') {
              tipo = 'categoria';
            } else if (segmentKey === 'canaisMidia') {
              tipo = 'midia';
            } else {
              tipo = segmentKey || null;
            }

            return (segment.segmentSelections || []).flatMap(selection => {
              const { cidades = [], subcategorias = [] } = selection;

              // Handle estado with cidades
              if (cidades.length > 0) {
                return cidades.map(cidade => ({
                  id: selection.id,
                  cidadeId: cidade.id,
                  tipo,
                }));
              }

              // Handle categorias with subcategorias
              if (subcategorias.length > 0) {
                return subcategorias.map(subcat => ({
                  id: selection.id,
                  subCategoriaId: subcat.id,
                  tipo,
                }));
              }

              // Handle simple segments (canaisMidia, estado without cities, etc.)
              return [
                {
                  id: selection.id,
                  tipo,
                },
              ];
            });
          });

          updateCampaignQuery.mutate({
            id: campaign.id,
            segments: segmentsWithTipo,
          });
        }
      }
    },
    1000,
    [segments, campaign?.segments]
  );

  useEffect(() => {
    if (isCnpjList) {
      setIsCNPJSelected(true);
      setSegments([
        {
          id: uniqueId(),
          segmentType:
            SEGMENTS.find(segment => segment.key === 'cnpj') ?? undefined,
        },
      ]);
      setBlockedSegments(
        SEGMENTS.filter(segment => segment.key !== 'cnpj').map(
          segment => segment.key
        )
      );
    }
  }, [isCnpjList]);

  return (
    <>
      {uploadStatus === 'loading' && (
        <LoadingOverlay message="Enviando planilha de CNPJ e CPF, isso pode levar alguns minutos..." />
      )}
      {deleteStatus === 'loading' && (
        <LoadingOverlay message="Excluindo lista de CNPJ e CPF, isso pode levar alguns minutos..." />
      )}
      <StyledCampaignFormSubtitle>Segmentação</StyledCampaignFormSubtitle>

      {segments.map((segmentField, index) => (
        <StyledCampaignFormRow key={`segment-row-${segmentField.id}`}>
          <SegmentRow
            key={`segment-row-${segmentField.id}`}
            segmentField={segmentField}
            index={index}
            isCnpjList={isCnpjList!}
            blockedSegments={blockedSegments}
            isFirstSelectionSegment={isFirstSelectionSegment}
            setSegments={setSegments}
            setIsCNPJSelected={setIsCNPJSelected}
            setSelectedState={setSelectedState}
            setSelectedCategories={setSelectedCategories}
          />

          {!segmentField.segmentType && (
            <TextField
              label="Selecione uma segmentação"
              disabled
              fullWidth
              variant="filled"
            />
          )}

          {segmentField.segmentType &&
            segmentField.segmentType.key !== 'cnpj' && (
              <SelectState
                id={segmentField.segmentType.key}
                label={segmentField.segmentType.name}
                segment={segmentField.segmentType.key as keyof CampaignSegment}
                onChange={value => {
                  if (segmentField?.segmentType?.name === 'Estado') {
                    setSelectedState(value);
                  }
                  if (segmentField?.segmentType?.name === 'Categorias') {
                    setSelectedCategories(value);
                  }
                  setSegments(prevSegments => {
                    const newSegments = [...prevSegments];
                    newSegments[index].segmentSelections = value;
                    return newSegments;
                  });
                }}
                value={segmentField.segmentSelections ?? []}
              />
            )}

          {segmentField.segmentType?.key === 'cnpj' && (
            <CnpjActionsRow
              onUploadClick={handleClick}
              onFileChange={handleFileChange}
              fileInputRef={fileInputRef}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onDownloadDefault={handleDownloadDefault}
              isCnpjList={isCnpjList!}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <SegmentActions
              onAddSegment={() => handleAddSegment(index)}
              onRemoveSegment={() => {
                if (segmentField?.segmentType?.name === 'Estado') {
                  setSelectedState([]);
                }
                if (segmentField?.segmentType?.name === 'Categorias') {
                  setSelectedCategories([]);
                }
                handleRemoveSegment(index);
              }}
              canAddSegment={segments.length <= 2 && !isCNPJSelected}
              canRemoveSegment={segments.length > 1 && !isCNPJSelected}
            />
          </Box>
        </StyledCampaignFormRow>
      ))}

      {selectedStates.length > 0 && (
        <StyledCampaignFormSubtitle>
          Selecione a cidade
        </StyledCampaignFormSubtitle>
      )}

      {selectedStates.map(state => {
        return (
          <CitySelection
            key={`city-selection-${state.id}`}
            state={state}
            segments={segments}
            onCityChange={value => {
              setSegments(prevSegments => {
                const newSegments = [...prevSegments];

                const stateSegmentIndex = newSegments.findIndex(
                  segment => segment.segmentType?.name === 'Estado'
                );

                if (stateSegmentIndex !== -1) {
                  const updatedSegmentSelections = newSegments[
                    stateSegmentIndex
                  ].segmentSelections?.map(selection => {
                    if (selection.id === state.id) {
                      return { ...selection, cidades: value };
                    }
                    return selection;
                  });

                  newSegments[stateSegmentIndex].segmentSelections =
                    updatedSegmentSelections;
                }

                // Build segments with proper tipo for each one
                const segmentsWithTipo = newSegments.flatMap(segment => {
                  const segmentKey = segment.segmentType?.key;

                  let tipo: string | null = null;
                  if (segmentKey === 'categorias') {
                    tipo = 'categoria';
                  } else if (segmentKey === 'canaisMidia') {
                    tipo = 'midia';
                  } else {
                    tipo = segmentKey || null;
                  }

                  return (segment.segmentSelections || []).flatMap(
                    selection => {
                      const { cidades = [], subcategorias = [] } = selection;

                      // Handle estado with cidades
                      if (cidades.length > 0) {
                        return cidades.map(cidade => ({
                          id: selection.id,
                          cidadeId: cidade.id,
                          tipo,
                        }));
                      }

                      // Handle categorias with subcategorias
                      if (subcategorias.length > 0) {
                        return subcategorias.map(subcat => ({
                          id: selection.id,
                          subCategoriaId: subcat.id,
                          tipo,
                        }));
                      }

                      // Handle simple segments
                      return [
                        {
                          id: selection.id,
                          tipo,
                        },
                      ];
                    }
                  );
                });

                if (campaign) {
                  updateCampaignQuery.mutate({
                    id: campaign.id,
                    segments: segmentsWithTipo,
                  });
                }

                return newSegments;
              });
            }}
          />
        );
      })}

      {selectedCategories.length > 0 && (
        <StyledCampaignFormSubtitle>
          Selecione a subcategoria
        </StyledCampaignFormSubtitle>
      )}

      {selectedCategories.map(category => {
        return (
          <SubcategorySelection
            key={`subcategory-selection-${category.id}`}
            category={category}
            segments={segments}
            onSubcategoryChange={value => {
              setSegments(prevSegments => {
                const newSegments = [...prevSegments];

                const categorySegmentIndex = newSegments.findIndex(
                  segment => segment.segmentType?.name === 'Categorias'
                );

                if (categorySegmentIndex !== -1) {
                  const updatedSegmentSelections = newSegments[
                    categorySegmentIndex
                  ].segmentSelections?.map(selection => {
                    if (selection.id === category.id) {
                      return { ...selection, subcategorias: value };
                    }
                    return selection;
                  });

                  newSegments[categorySegmentIndex].segmentSelections =
                    updatedSegmentSelections;
                }

                // Build segments with proper tipo for each one
                const segmentsWithTipo = newSegments.flatMap(segment => {
                  const segmentKey = segment.segmentType?.key;

                  let tipo: string | null = null;
                  if (segmentKey === 'categorias') {
                    tipo = 'categoria';
                  } else if (segmentKey === 'canaisMidia') {
                    tipo = 'midia';
                  } else {
                    tipo = segmentKey || null;
                  }

                  return (segment.segmentSelections || []).flatMap(
                    selection => {
                      const { cidades = [], subcategorias = [] } = selection;

                      // Handle estado com cidades
                      if (cidades.length > 0) {
                        return cidades.map(cidade => ({
                          id: selection.id,
                          cidadeId: cidade.id,
                          tipo,
                        }));
                      }

                      // Handle categorias with subcategorias
                      if (subcategorias.length > 0) {
                        return subcategorias.map(subcat => ({
                          id: selection.id,
                          subCategoriaId: subcat.id,
                          tipo,
                        }));
                      }

                      // Handle simple segments
                      return [
                        {
                          id: selection.id,
                          tipo,
                        },
                      ];
                    }
                  );
                });

                if (campaign) {
                  updateCampaignQuery.mutate({
                    id: campaign.id,
                    segments: segmentsWithTipo,
                  });
                }

                return newSegments;
              });
            }}
          />
        );
      })}

      {uploadStatus === 'success' && (
        <CampaignUpdatedExcelModal
          mensagem={responseInfo.upload?.mensagem}
          handleUpdateAfterUpload={handleUpdateAfterUpload}
          onCloseModal={handleOnCloseModalUploadSuccess}
        />
      )}

      {isDeleteCnpjList && (
        <CampaignDeleteCnpjExcelModal
          message={responseInfo.delete?.message}
          handleUpdateAfterDelete={handleUpdateAfterDelete}
          onCloseModal={handleOnCloseModalDeleteSuccess}
        />
      )}

      {uploadStatus === 'error' && (
        <CampaignErrorExcelModal onCloseModal={handleOnCloseModalError} />
      )}
    </>
  );
}
